import {
	ExtrudeGeometry,
	Mesh,
	MeshStandardMaterial,
	Shape,
	Vector2
} from 'three';

import type { GeometryBuilder } from '../geometry-builder.class';
import type { AddPanelInfo } from '../../../../model/add-panel-info';
import type { IV2 } from '../../../../interfaces';
import { TextureType } from '../../../../model/enums';
import {
	getActualQuality,
	applyGeometryQualityOptimizations,
	applyLowQualityMaterialOptimizations,
	createSurroundMaterialSet,
	splitExtrudeCaps
} from '../helpers';

/**
 * Get material by texture key
 */
export const getMaterialCfn = (
	self: GeometryBuilder,
	{ textureKey }: { textureKey?: string }
): MeshStandardMaterial => {
	if (textureKey && self.configAccess.materials.has(textureKey)) {
		const mat = self.configAccess.materials.get(textureKey)!.clone();
		return mat;
	}

	// Default material
	console.warn('GeometryBuilder.getMaterial: Material not found for:', textureKey, 'Available keys:', Array.from(self.configAccess.materials.keys()));
	return new MeshStandardMaterial({
		color: 0xcccccc,
		wireframe: self.configAccess.qualitySettings?.wireframe ?? false
	});
};

/**
 * Create extruded geometry from vertices
 */
export const createExtrudeGeometryCfn = (
	self: GeometryBuilder,
	{ api }: { api: AddPanelInfo }
): ExtrudeGeometry => {
	const depth = api.size?.depth || 1;
	const points: Vector2[] = api.vertices.map((v: IV2) => new Vector2(v.x, v.y));

	// Create shape from vertices
	const shape = new Shape();
	if (points.length > 0) {
		shape.moveTo(points[0].x, points[0].y);
		for (let i = 1; i < points.length; i++) {
			shape.lineTo(points[i].x, points[i].y);
		}
		shape.lineTo(points[0].x, points[0].y); // Close the shape
	}

	// Create extrude geometry
	const geometry = new ExtrudeGeometry(shape, {
		depth,
		bevelEnabled: false
	});

	// Apply quality-based geometry optimizations
	const actualQuality = getActualQuality(self.configAccess.renderQuality);
	applyGeometryQualityOptimizations(geometry, actualQuality);

	// Center geometry for Surround panels
	if (api.panelType === TextureType.Surround) {
		geometry.center();
	}

	return geometry;
};

/**
 * Create mesh from geometry and material
 */
export const createMeshFromGeometryCfn = (
	self: GeometryBuilder,
	{ api, geometry, mat }: {
		api: AddPanelInfo;
		geometry: ExtrudeGeometry;
		mat: MeshStandardMaterial;
	}
): Mesh => {
	const actualQuality = getActualQuality(self.configAccess.renderQuality);
	let mesh: Mesh;

	if (api.panelType === TextureType.Counter) {
		// Counters use material array for chiseled edges
		const materials = [mat.clone(), mat.clone()];
		materials[0].normalMap = null;
		materials[0].needsUpdate = true;

		if (self.configAccess.qualitySettings?.wireframe) {
			materials[0].wireframe = true;
			materials[1].wireframe = true;
		}

		// Low quality optimizations
		if (actualQuality <= 3) {
			materials.forEach(m => {
				applyLowQualityMaterialOptimizations(m, actualQuality);
			});
		}

		mesh = new Mesh(geometry, materials);
	} else if (api.panelType === TextureType.Surround && splitExtrudeCaps(geometry, true, api.size?.depth || 1)) {
		// Only the outward cap carries the finish -- the one face that touches the mould in manufacturing
		const texturedMat = actualQuality <= 3 ? mat.clone() : mat;
		if (actualQuality <= 3) {
			applyLowQualityMaterialOptimizations(texturedMat, actualQuality);
		}
		const surroundMaterials = createSurroundMaterialSet(texturedMat);

		if (self.configAccess.qualitySettings?.wireframe) {
			surroundMaterials.forEach((surroundMat) => { surroundMat.wireframe = true; });
		}

		mesh = new Mesh(geometry, surroundMaterials);
	} else {
		// Single material
		if (actualQuality <= 3) {
			const panelMat = mat.clone();
			panelMat.normalMap = null;
			applyLowQualityMaterialOptimizations(panelMat, actualQuality);
			mesh = new Mesh(geometry, panelMat);
		} else {
			mesh = new Mesh(geometry, mat);
		}
	}

	// Apply shadow settings
	const enableShadows = self.configAccess.qualitySettings?.shadowMapEnabled ?? true;
	mesh.castShadow = enableShadows;
	mesh.receiveShadow = enableShadows;

	// Set panel category for userData
	const panelCategory = api.panelType === TextureType.Counter
		? 'countertop'
		: (api.panelType === TextureType.Trim ? 'corner' : 'panel');

	mesh.userData = {
		id: api.id,
		type: panelCategory,
		textureType: api.panelType,
		islandIndex: api.islandIndex
	};

	return mesh;
};

/**
 * Apply transforms to mesh
 */
export const applyTransformsCfn = (
	_self: GeometryBuilder,
	{ mesh, api }: { mesh: Mesh; api: AddPanelInfo }
): void => {
	// Apply rotation (degrees to radians)
	if (api.rotation) {
		mesh.rotateX((api.rotation.x || 0) * Math.PI / 180);
		mesh.rotateY((api.rotation.y || 0) * Math.PI / 180);
		mesh.rotateZ((api.rotation.z || 0) * Math.PI / 180);
	}

	// Apply position
	if (api.center) {
		const depth = api.size?.depth || 1;
		mesh.position.x = api.center.x;
		mesh.position.y = api.center.y + (api.panelType === TextureType.Surround ? 0 : depth / 2);
		mesh.position.z = api.center.z;
	}
};
